/** Best-effort same-browser installation choice for onboarding attribution.
 * Not a device-side proof: users may choose another method or verify elsewhere.
 */
export type InstallationMethod = "npx" | "script" | "script_cmd" | "download";
const KEY = "dc.install.choice.v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function recordInstallationChoice(userId: string | undefined, os: string, method: InstallationMethod): void {
    if (!userId || typeof window === "undefined") {
        return;
    }
    try {
        window.localStorage.setItem(
            KEY,
            JSON.stringify({
                userId,
                os,
                method,
                at: Date.now(),
            }),
        );
    } catch {
        /* Browsers can block storage. */
    }
}

export function recentInstallationChoice(userId: string | undefined): {
    os: string;
    method: InstallationMethod;
} | null {
    if (!userId || typeof window === "undefined") {
        return null;
    }
    try {
        const stored = window.localStorage.getItem(KEY);
        if (!stored) {
            return null;
        }
        const choice = JSON.parse(stored);
        if (typeof choice.at !== "number" || Date.now() - choice.at > MAX_AGE_MS || Date.now() < choice.at) {
            window.localStorage.removeItem(KEY);
            return null;
        }
        if (choice.userId !== userId) {
            return null;
        }
        if (
            ![
                "npx",
                "script",
                "script_cmd",
                "download",
            ].includes(choice.method)
        ) {
            return null;
        }
        if (
            ![
                "windows",
                "mac",
                "linux",
            ].includes(choice.os)
        ) {
            return null;
        }
        return {
            os: choice.os,
            method: choice.method,
        };
    } catch {
        return null;
    }
}
